import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Layout } from '@components/layout/Layout';
import { ProtectedRoute } from '@components/common/ProtectedRoute';

// Public Pages
import { Home } from '@pages/public/Home';
import { About } from '@pages/public/About';
import { Requirements } from '@pages/public/Requirements';
import { TestingCenters } from '@pages/public/TestingCenters';
import { Contact } from '@pages/public/Contact';
import { Terms } from '@pages/public/Terms';

// Auth Pages
import { Login } from '@pages/auth/Login';
import { Register } from '@pages/auth/Register';
import { ForgotPassword } from '@pages/auth/ForgotPassword';

// Applicant Pages
import { ApplicantDashboard } from '@pages/applicant/Dashboard';
import { Profile } from '@pages/applicant/Profile';
import { ApplicationWizard } from '@pages/applicant/ApplicationWizard';
import { DocumentManager } from '@pages/applicant/DocumentManager';
import { BookingCenter } from '@pages/applicant/BookingCenter';
import { AppointmentHistory } from '@pages/applicant/AppointmentHistory';
import { StatusTracker } from '@pages/applicant/StatusTracker';

// Admin Pages
import { AdminDashboard } from '@pages/admin/Dashboard';
import { ApplicantList } from '@pages/admin/ApplicantList';
import { ApplicantDetail } from '@pages/admin/ApplicantDetail';
import { EnrolmentReview } from '@pages/admin/EnrolmentReview';
import { CenterManagement } from '@pages/admin/CenterManagement';
import { BookingManagement } from '@pages/admin/BookingManagement';
import { UserManagement } from '@pages/admin/UserManagement';
import { AuditLogs } from '@pages/admin/AuditLogs';
import { Analytics } from '@pages/admin/Analytics';
import { Settings } from '@pages/admin/Settings';

// Verification Pages
import { LicenseVerification } from '@pages/verification/LicenseVerification';
import { InspectorLogin } from '@pages/verification/InspectorLogin';
import { InspectorVerify } from '@pages/verification/InspectorVerify';

// Error Pages
import { NotFound } from '@pages/errors/NotFound';
import { ServerError } from '@pages/errors/ServerError';
import { Unauthorized } from '@pages/errors/Unauthorized';

export const router = createBrowserRouter([
  // Verification Portal Routes (Standalone - No Layout/Header)
  {
    path: 'licver/:hashcode',
    element: <LicenseVerification />,
    errorElement: <ServerError />,
  },
  {
    path: 'inspector/login',
    element: <InspectorLogin />,
    errorElement: <ServerError />,
  },
  {
    path: 'inspector/verify/:hashcode',
    element: <InspectorVerify />,
    errorElement: <ServerError />,
  },

  // Main Application Routes (With Layout/Header)
  {
    path: '/',
    element: <Layout />,
    errorElement: <ServerError />,
    children: [
      // Public Routes
      {
        index: true,
        element: <Home />,
      },
      {
        path: 'about',
        element: <About />,
      },
      {
        path: 'requirements',
        element: <Requirements />,
      },
      {
        path: 'centers',
        element: <TestingCenters />,
      },
      {
        path: 'contact',
        element: <Contact />,
      },
      {
        path: 'terms',
        element: <Terms />,
      },

      // Auth Routes
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'register',
        element: <Register />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPassword />,
      },

      // Applicant Routes (Protected)
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute requiredRoles={['APPLICANT']}>
            <ApplicantDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <ProtectedRoute requiredRoles={['APPLICANT']}>
            <Profile />
          </ProtectedRoute>
        ),
      },
      {
        path: 'apply',
        element: (
          <ProtectedRoute requiredRoles={['APPLICANT']}>
            <ApplicationWizard />
          </ProtectedRoute>
        ),
      },
      {
        path: 'documents',
        element: (
          <ProtectedRoute requiredRoles={['APPLICANT']}>
            <DocumentManager />
          </ProtectedRoute>
        ),
      },
      {
        path: 'booking',
        element: (
          <ProtectedRoute requiredRoles={['APPLICANT']}>
            <BookingCenter />
          </ProtectedRoute>
        ),
      },
      {
        path: 'appointments',
        element: (
          <ProtectedRoute requiredRoles={['APPLICANT']}>
            <AppointmentHistory />
          </ProtectedRoute>
        ),
      },
      {
        path: 'status',
        element: (
          <ProtectedRoute requiredRoles={['APPLICANT']}>
            <StatusTracker />
          </ProtectedRoute>
        ),
      },

      // Admin Routes (Protected)
      {
        path: 'admin',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute requiredRoles={['ADMIN', 'OPS']}>
                <AdminDashboard />
              </ProtectedRoute>
            ),
          },
          {
            path: 'applicants',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN', 'OPS']}>
                <ApplicantList />
              </ProtectedRoute>
            ),
          },
          {
            path: 'applicants/:id',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN', 'OPS']}>
                <ApplicantDetail />
              </ProtectedRoute>
            ),
          },
          {
            path: 'enrolments',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN', 'OPS']}>
                <EnrolmentReview />
              </ProtectedRoute>
            ),
          },
          {
            path: 'centers',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN']}>
                <CenterManagement />
              </ProtectedRoute>
            ),
          },
          {
            path: 'bookings',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN', 'OPS']}>
                <BookingManagement />
              </ProtectedRoute>
            ),
          },
          {
            path: 'users',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN']}>
                <UserManagement />
              </ProtectedRoute>
            ),
          },
          {
            path: 'audit',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN']}>
                <AuditLogs />
              </ProtectedRoute>
            ),
          },
          {
            path: 'analytics',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN']}>
                <Analytics />
              </ProtectedRoute>
            ),
          },
          {
            path: 'settings',
            element: (
              <ProtectedRoute requiredRoles={['ADMIN']}>
                <Settings />
              </ProtectedRoute>
            ),
          },
        ],
      },

      // Error Routes
      {
        path: 'unauthorized',
        element: <Unauthorized />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]);

export const AppRouter = () => {
  return <RouterProvider router={router} />;
};